import { LANG } from "./symbols";
import { format, addMinutes, parse } from "date-fns";

export const MONTH_NAMES_LONG = Array.from({ length: 12 }, (_, month) =>
	new Date(2024, month, 3).toLocaleDateString(LANG, { month: "long" })
);
export const MONTH_NAMES_SHORT = Array.from({ length: 12 }, (_, month) =>
	new Date(2024, month, 3).toLocaleDateString(LANG, { month: "short" })
);
export const DAY_NAMES_LONG = Array.from({ length: 7 }, (_, offset) =>
	new Date(2024, 0, 7 + offset).toLocaleDateString(LANG, { weekday: "long" })
);
export const DAY_NAMES_SHORT = Array.from({ length: 7 }, (_, offset) =>
	new Date(2024, 0, 7 + offset).toLocaleDateString(LANG, { weekday: "short" })
);

export const getAdjustedDay = (date, firstDayOfWeek) =>
	(date.getDay() - (firstDayOfWeek ?? 1) + 7) % 7;

export const parseDate = (format, date, utc) => {
	try {
		const dateObj = new Date(date);
		let parsedDate = "";
		if (isNaN(dateObj.getTime()))
			throw new Error(`Not possible to build a date object with ${date}`);

		const WEEK_DAY = utc ? dateObj.getUTCDay() : dateObj.getDay();
		const DATE = utc ? dateObj.getUTCDate() : dateObj.getDate();
		const MONTH = utc ? dateObj.getUTCMonth() : dateObj.getMonth();
		const YEAR = utc ? dateObj.getUTCFullYear() : dateObj.getFullYear();
		const HOURS = utc ? dateObj.getUTCHours() : dateObj.getHours();
		const MINUTES = utc ? dateObj.getUTCMinutes() : dateObj.getMinutes();
		const SECONDS = utc ? dateObj.getUTCMinutes() : dateObj.getMinutes();

		switch (format) {
			case "Mon":
				parsedDate = DAY_NAMES_SHORT[WEEK_DAY];
				break;
			case "d":
				parsedDate = String(DATE);
				break;
			case "November yyyy":
				parsedDate = `${MONTH_NAMES_LONG[MONTH]} ${YEAR}`;
				break;
			case "November":
				parsedDate = `${MONTH_NAMES_LONG[MONTH]}`;
				break;
			case "Nov":
				parsedDate = MONTH_NAMES_SHORT[MONTH];
				break;
			case "d Nov yyyy":
				parsedDate = `${DATE} ${MONTH_NAMES_SHORT[MONTH]} ${YEAR}`;
				break;
			case "Mon d November yyyy":
				parsedDate = `${DAY_NAMES_SHORT[WEEK_DAY]} ${DATE} ${MONTH_NAMES_LONG[MONTH]} ${YEAR}`;
				break;
			case "Monday d November yyyy":
				parsedDate = `${DAY_NAMES_LONG[WEEK_DAY]} ${DATE} ${MONTH_NAMES_LONG[MONTH]} ${YEAR}`;
				break;
			case "d Nov 'yy":
				parsedDate = `${DATE} ${MONTH_NAMES_SHORT[MONTH]} '${String(
					YEAR
				).substring(2)}`;
				break;
			case "yyyy-mm-dd":
				parsedDate = `${YEAR}-${String(MONTH + 1).padStart(
					2,
					"0"
				)}-${String(DATE).padStart(2, "0")}`;
				break;
			case "yyyy-mm-dd hh:mm:ss":
				parsedDate = `${YEAR}-${String(MONTH + 1).padStart(
					2,
					"0"
				)}-${String(DATE).padStart(2, "0")} ${String(HOURS).padStart(
					2,
					"0"
				)}:${String(MINUTES).padStart(2, "0")}:${String(
					SECONDS
				).padStart(2, "0")}`;
				break;
			case "hh:mm":
				parsedDate = `${String(HOURS).padStart(2, "0")}:${String(
					MINUTES
				).padStart(2, "0")}`;
				break;
			default:
				break;
		}

		return parsedDate;
	} catch (error) {
		return "Invalid Date";
	}
};

export const getWeekSlotsTillMonthEnd = (date, firstDayOfWeek) => {
	const currentDay = date.getDate();
	const firstWeekDay = getAdjustedDay(date);
	const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
	const lastWeekDay = getAdjustedDay(lastDay);
	const remainingDays = lastDay.getDate() - currentDay + 1;
	const totalWeekSlots = remainingDays + firstWeekDay + (6 - lastWeekDay);
	return Math.ceil(totalWeekSlots / 7);
};

export const getMonthDifference = (date1, date2) => {
	if (date1 === undefined || date2 === undefined) return 0;
	const firstDate = new Date(date1);
	const secondDate = new Date(date2);
	if (
		isNaN(firstDate.getTime()) ||
		isNaN(secondDate.getTime()) ||
		date1 === 0 ||
		date2 === 0
	)
		return 0;
	const year1 = firstDate.getFullYear();
	const month1 = firstDate.getMonth();
	const year2 = secondDate.getFullYear();
	const month2 = secondDate.getMonth();
	const yearDifference = year2 - year1;
	const monthDifference = month2 - month1;
	return yearDifference * 12 + monthDifference;
};

export function orderCentersByDistance(centers, coords) {
	const centersWithDistance = centers.map((center) =>
		addDistanceToCenter(center, coords),
	);

	const highAvailability = centersWithDistance.filter(
		(c) => c.availability === "high",
	);
	const others = centersWithDistance.filter(
		(c) => c.availability !== "high",
	);

	highAvailability.sort((a, b) => a.distance - b.distance);
	others.sort((a, b) => a.distance - b.distance);

	const closestHigh = highAvailability[0];

	if (closestHigh && closestHigh.distance <= 7) {
		const remaining = [...highAvailability.slice(1), ...others].sort(
			(a, b) => a.distance - b.distance,
		);
		return [closestHigh, ...remaining];
	}

	return [...highAvailability, ...others].sort((a, b) => a.distance - b.distance);
}

export function addDistanceToCenter(center, coords) {
	if (!coords) {
		return center;
	}
	const centerCoords = {
		lat: center.latitude,
		lng: center.longitude,
	};
	return {
		...center,
		distance: haversineDistance(coords, centerCoords),
	};
}

function haversineDistance(coord1, coord2) {
	const R = 6371;
	const dLat = toRadians(coord2.lat - coord1.lat);
	const dLon = toRadians(coord2.lng - coord1.lng);
	const lat1 = toRadians(coord1.lat);
	const lat2 = toRadians(coord2.lat);
	const a =
		Math.sin(dLat / 2) ** 2 +
		Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
	const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	return R * c;
}

function toRadians(deg) {
	return (deg * Math.PI) / 180;
}

export function formatRange(time, offsetStart, offsetEnd) {
	const baseTime = parse(time, "HH:mm", new Date());
	const start = format(addMinutes(baseTime, offsetStart), "HH:mm");
	const end = format(addMinutes(baseTime, offsetEnd), "HH:mm");
	return `${start} - ${end}`;
}

export function sendEventToParent(eventType, data, flow) {
	if (flow !== "mapfre") return;

	const startingTime = data?.time ? data.time.split('-')[0] : null
	const payload = {
		type: eventType,
		data: {
			status: eventType,
			mobile_unit: false,
			appointment: data?.code ?? null,
			datatime: data?.date
				? data?.time
					? `${data.date}T${startingTime}`
					: data.date
				: null,
			branch: {
				id: data?.centerCode ?? null,
				name: data?.centerName ?? null,
				address: data?.centerAddress ?? null,
				city: data?.centerCity ?? null,
				province: data?.centerProvince ?? null,
				postalCode: data?.centerPostalCode ?? null,
			},
			service_pretty: data?.damageArea ?? null,
			msg: null,
		},
	};

	// 1) iOS (WKWebView con InAppBrowser)
	if (
		typeof window !== "undefined" &&
		window.webkit?.messageHandlers?.cordova_iab?.postMessage
	) {
		window.webkit.messageHandlers.cordova_iab.postMessage(
			JSON.stringify(payload)
		);
		// console.log("iOS message sent:", payload);
		return;
	}

	// 2) Android (InAppBrowser)
	if (
		typeof window !== "undefined" &&
		window.cordova_iab?.postMessage &&
		typeof window.cordova_iab.postMessage === "function"
	) {
		window.cordova_iab.postMessage(payload);
		// console.log("Android message sent:", payload);
		return;
	}

	// 3) Navegador normal/iframe
	if (
		typeof window !== "undefined" &&
		window.parent &&
		window.parent !== window
	) {
		window.parent.postMessage(payload, "*");
		// console.log("postMessage sent to parent frame:", payload);
		return;
	}

	console.warn("No se detectó puente de InAppBrowser ni parent frame.", payload);
}

export const getInitialData = () => {
	const params = new URLSearchParams(window.location.search);
	const locator = params.get("locator") || null;
	const origin = params.get("origin") || undefined;
	const centerCode = params.get("centerCode") || null;

	const stored = sessionStorage.getItem("Data");

	if (stored) {
		try {
			const expires = sessionStorage.getItem("Data_expires");
			if (expires && Date.now() > parseInt(expires, 10)) {
				sessionStorage.removeItem("Data");
				sessionStorage.removeItem("Data_expires");
			} else {
				const parsed = JSON.parse(stored);

				if (parsed.centerFromParam && !centerCode) {
					sessionStorage.removeItem("Data");
					sessionStorage.removeItem("Data_expires");
				} else {
					const stripped = {
						...(parsed.plate ? { plate: parsed.plate } : {}),
						...(parsed.plateNumber ? { plateNumber: parsed.plateNumber } : {}),
						...(parsed.phone ? { phone: parsed.phone } : {}),
						...(parsed.phoneNumber ? { phoneNumber: parsed.phoneNumber } : {}),
						...(parsed.prefixCode ? { prefixCode: parsed.prefixCode } : {}),
						...(parsed.shortPhone ? { shortPhone: parsed.shortPhone } : {}),
					};

					const result = {
						...stripped,
						...(origin ? { origin } : {}),
						...(centerCode ? { centerCode, centerFromParam: true } : {}),
					};

					result.locator = locator || parsed.locator || null;

					sessionStorage.setItem("Data", JSON.stringify(result));
					return result;
				}
			}
		} catch {
			sessionStorage.removeItem("Data");
			sessionStorage.removeItem("Data_expires");
		}
	}

	const base = {};
	if (locator) base.locator = locator;
	if (origin) base.origin = origin;
	if (centerCode) { base.centerCode = centerCode; base.centerFromParam = true; }
	return base;
};

export const sendBooking = async (locator) => {
	const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";

	try {
		const res = await fetch(`${apiUrl}/send/${locator}`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
		});
		const data = await res.json();
		return data;
	} catch (err) {
		console.error("Error sending booking:", err);
		return false;
	}
};

export const getAvailableAppointments = async (centerCode, locator, maxAppointments = 3, maxDays = 10) => {
	const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3000";

	try {
		const daysRes = await fetch(
			`${apiUrl}/availability/by-day/${centerCode}?locator=${locator}`
		);
		const daysData = await daysRes.json();

		if (daysData.result !== "ok") {
			console.error(`getAvailableAppointments: /availability/by-day/ failed for ${centerCode}, result: ${daysData.result}`);
			return [];
		}

		const occupiedDays = (daysData.days || []).map(d => d.split("T")[0]);
		const occupiedSet = new Set(occupiedDays);

		const appointments = [];
		const today = new Date();

		for (let i = 0; i < maxDays && appointments.length < maxAppointments; i++) {
			const checkDate = new Date(today);
			checkDate.setDate(today.getDate() + i);
			const dateStr = checkDate.toISOString().split("T")[0];

			if (occupiedSet.has(dateStr)) {
				continue;
			}

			const hoursRes = await fetch(
				`${apiUrl}/availability/${centerCode}/${dateStr}?locator=${locator}`
			);
			const hoursData = await hoursRes.json();

			if (hoursData.result !== "ok" || !hoursData.shifts?.data) {
				console.error(`getAvailableAppointments: /availability/ failed for ${centerCode} on ${dateStr}, result: ${hoursData.result}, has shifts: ${!!hoursData.shifts?.data}`);
				continue;
			}

			const slots = hoursData.shifts.data;

			for (const turn of slots) {
				for (const slot of turn) {
					if (slot.available !== true) continue;

					const time = slot.dropoff;
					if (!time) continue;

					const formattedDate = checkDate.toLocaleDateString("es-ES", {
						day: "numeric",
						month: "short",
					});

				appointments.push({
					date: formattedDate,
					time: time,
					fullDate: dateStr,
					fullTime: time,
					pickup: slot.pickup || null,
				});

					if (appointments.length >= maxAppointments) break;
				}
				if (appointments.length >= maxAppointments) break;
			}
		}

		return appointments;
	} catch (err) {
		console.error("Error getting appointments:", err);
		return [];
	}
};

export function extractTimeData(apt) {
	const data = {
		time: null,
		startTime: null,
		endTime: null,
	}
	if (typeof apt === "object") {
		data.startTime = apt.dropoff;
		data.endTime = apt.pickup;
		data.time = apt.dropoff + "-" + apt.pickup;
	} else {
		data.startTime = apt;
	}
	return data
}

